import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getDepositAccountConnectionStatus,
  type DepositAccountConnectionStatus,
} from '@/supabase/deposit/service/depositAccountService';
import { useAuth } from '@/contexts/AuthContext';

type AccountStatus = DepositAccountConnectionStatus | 'loading' | 'unknown';

export function useDepositAccountStatus() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [status, setStatus] = useState<AccountStatus>('loading');
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const userIdRef = useRef(userId);
  const requestSequenceRef = useRef(0);
  userIdRef.current = userId;

  const refresh = useCallback(async () => {
    if (!userId) {
      setStatus('unlinked');
      setLoadedUserId(null);
      return;
    }

    const requestUserId = userId;
    const sequence = ++requestSequenceRef.current;
    try {
      const nextStatus = await getDepositAccountConnectionStatus();
      if (sequence !== requestSequenceRef.current || userIdRef.current !== requestUserId) return;
      setStatus(nextStatus);
      setLoadedUserId(requestUserId);
    } catch {
      if (sequence !== requestSequenceRef.current || userIdRef.current !== requestUserId) return;
      setStatus('unknown');
      setLoadedUserId(requestUserId);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      requestSequenceRef.current++;
      setStatus('unlinked');
      setLoadedUserId(null);
      return;
    }
    void refresh();
  }, [refresh, userId]);

  return {
    status: loadedUserId === userId ? status : 'loading',
    refresh,
  };
}
